import { DiagnosisTypeCode } from '@quromedical/fhir-common';
import {
  Admission,
  AdmissionCreateRequest,
  admissionRequestSchemaFields,
  AllergyCode,
} from '@quromedical/models';
import { compact } from '@quromedical/utils';
import { Row } from 'components/base';
import { CrudForm } from 'components/form';
import { Field, SelectOption } from 'components/types';
import { AllergyListForm } from 'core/allergy';
import { Card, FetcherSearchOptions, Text, Visible } from 'design-system';
import { getDefinedState, useObjectState } from 'hooks/useObjectState';
import { chronicIcd10CodeFetcher } from 'integration';
import { usePatientVitals } from 'providers/PatientVitalsProvider';
import React, { useMemo } from 'react';
import { strings } from 'strings';
import { ObjectType } from 'validation';
import * as yup from 'yup';

import { createOxygenField, createPregnancyField, getOxygenValue } from './internal';

export type AdmissionClinicalData = Pick<
  AdmissionCreateRequest,
  'oxygen' | 'diagnosis' | 'pregnancy' | 'allergy'
>;

type ICD10Fetcher = (search?: FetcherSearchOptions) => Promise<SelectOption[]>;

interface ReasonOfAdmissionCodes {
  acute?: string[];
  reasonOfAdmission?: string[];
  chronic?: string[];
}
interface AdmissionClinicalFormProps {
  showErrors?: boolean;
  /**
   * Returns undefined if the data is incomplete or invalid. This is to simplify error checking and
   * validation at a parent level - no data means no valid data
   */
  onValidChange: (data?: AdmissionClinicalData) => void;
  icd10CodeFetcher: ICD10Fetcher;
  rfaIcd10Codes?: ReasonOfAdmissionCodes;
  icd10Codes?: string[];
  initialValues?: Partial<Admission>;
}

const diagnosisSchema = yup
  .object<ObjectType<DiagnosisFields>>({
    acute: admissionRequestSchemaFields.icd10Codes,
    chronic: admissionRequestSchemaFields.icd10Codes,
    reasonOfAdmission: yup.array(yup.string().required()).min(1).required(),
  })
  .required();

const oxygenSchema = yup
  .object<ObjectType<OxygenFields>>({
    oxygen: admissionRequestSchemaFields.oxygen,
  })
  .required();

const pregnancySchema = admissionRequestSchemaFields.pregnancy;

const createField = (
  icd10CodeFetcher: ICD10Fetcher,
  data?: ReasonOfAdmissionCodes
): Field<DiagnosisFields>[] => [
  {
    heading: strings.HeadingReasonOfAdmission,
    subfields: [
      {
        key: 'reasonOfAdmission',
        type: 'combobox-multiple',
        label: strings.DiagnosisLabelRFA,
        fetcher: icd10CodeFetcher,
        searchable: true,
        initialSelection: data?.reasonOfAdmission?.map(code => ({
          display: code,
          value: code,
        })),
      },
      {
        key: 'chronic',
        type: 'combobox-multiple',
        label: strings.DiagnosisLabelChronic,
        fetcher: chronicIcd10CodeFetcher,
        initialSelection: data?.chronic?.map(code => ({
          display: code,
          value: code,
        })),
        searchable: true,
      },
    ],
  },
  {
    heading: strings.HeadingDuringOfAdmission,
    subfields: [
      {
        key: 'acute',
        type: 'combobox-multiple',
        initialSelection: data?.acute?.map(code => ({
          display: code,
          value: code,
        })),
        label: strings.DiagnosisLabelAcute,
        fetcher: icd10CodeFetcher,
        searchable: true,
      },
    ],
  },
];

interface OxygenFields {
  oxygen?: number;
  hasOxygen: boolean;
}
interface DiagnosisFields {
  acute?: string[];
  reasonOfAdmission?: string[];
  chronic?: string[];
  icd10Codes?: string[];
  primaryIcd10Code?: string[];
}
interface PregnancyFields {
  isPregnant: boolean;
  lactation: boolean;
  edd?: string;
}

interface ClinicalSection {
  oxygen?: OxygenFields;
  diagnosis?: DiagnosisFields;
  pregnancy?: PregnancyFields;
  allergy?: AllergyCode[];
}
export const mapDiagnosesToDiagnosisTypeCode = (diagnosis: DiagnosisFields | undefined) => {
  if (!diagnosis) {
    return [];
  }
  const acute = diagnosis.acute?.map(a => ({
    condition: a,
    type: DiagnosisTypeCode.acute,
  }));
  const chronic = diagnosis.chronic?.map(a => ({
    condition: a,
    type: DiagnosisTypeCode.chronic,
  }));
  const reasonOfAdmission = diagnosis.reasonOfAdmission?.map(a => ({
    condition: a,
    type: DiagnosisTypeCode.rfa,
  }));
  const icd10Codes = diagnosis.icd10Codes?.map(a => ({
    condition: a,
    type: DiagnosisTypeCode.secondary,
  }));
  const primaryIcd10Code = diagnosis.primaryIcd10Code?.map(a => ({
    condition: a,
    type: DiagnosisTypeCode.primary,
  }));

  return compact([acute, chronic, reasonOfAdmission, icd10Codes, primaryIcd10Code]).flat();
};

export const AdmissionClinicalForm: React.FC<AdmissionClinicalFormProps> = ({
  showErrors = false,
  onValidChange,
  icd10CodeFetcher,
  initialValues,
  rfaIcd10Codes,
}) => {
  const context = usePatientVitals();

  const patient = context.patient?.general;
  const isFemale = patient?.gender === 'female';

  const state = useObjectState<ClinicalSection>(
    {
      oxygen: { hasOxygen: false },
      pregnancy: { isPregnant: false, lactation: false },
    },
    change => {
      const defined = getDefinedState(change, ['diagnosis']);

      if (!defined) {
        onValidChange(undefined);
      } else {
        const oxygen = change.oxygen?.oxygen;
        onValidChange({
          oxygen: getOxygenValue(oxygen !== undefined, oxygen),
          diagnosis: mapDiagnosesToDiagnosisTypeCode(defined.diagnosis),
          pregnancy: change.pregnancy
            ? {
                isPregnant: change.pregnancy?.isPregnant,
                edd: change.pregnancy?.edd,
                lactation: change.pregnancy?.lactation,
              }
            : undefined,
          allergy: change.allergy,
        });
      }
    }
  );

  const icd10Field = useMemo(
    () => createField(icd10CodeFetcher, rfaIcd10Codes),
    [icd10CodeFetcher, rfaIcd10Codes]
  );

  const oxygenFields = useMemo(
    () => [createOxygenField(state.value.oxygen?.hasOxygen)],
    [state.value]
  );

  const pregnancyFields = useMemo(
    () => createPregnancyField(state.value.pregnancy?.isPregnant),
    [state.value]
  );

  const initialData = useMemo(() => rfaIcd10Codes || {}, [rfaIcd10Codes]);
  const hasOxygen = initialValues?.oxygen ? !!initialValues.oxygen : false;

  const isPregnant = Boolean(initialValues?.pregnancy?.isPregnant);

  const lactation = Boolean(initialValues?.pregnancy?.lactation);

  return (
    <Card unsetZIndex>
      <Row>
        <Text variant="heading">{strings.AdmissionClinicalFormTitle}</Text>
      </Row>

      <CrudForm<DiagnosisFields>
        cardProps={{ unsetZIndex: true }}
        validateOnMount
        validateOnChange
        initialValues={initialData}
        fields={icd10Field}
        validationSchema={diagnosisSchema}
        showErrors={showErrors}
        onValidChange={state.set('diagnosis')}
        onChangeDebounceTime={0}
        showSubmitButton={false}
        hasCard={false}
        titleVariant="subheading"
        includeHeading={true}
      />

      <CrudForm<OxygenFields>
        cardProps={{ unsetZIndex: true }}
        validateOnMount
        validateOnChange
        initialValues={{ hasOxygen: hasOxygen, oxygen: initialValues?.oxygen?.value }}
        fields={oxygenFields}
        validationSchema={oxygenSchema}
        showErrors={showErrors}
        onChange={state.set('oxygen')}
        onChangeDebounceTime={0}
        showSubmitButton={false}
        hasCard={false}
      />
      <Visible if={isFemale}>
        <CrudForm<PregnancyFields>
          cardProps={{ unsetZIndex: true }}
          validateOnMount
          validateOnChange
          initialValues={{
            isPregnant: isPregnant,
            edd: initialValues?.pregnancy?.edd,
            lactation: lactation,
          }}
          fields={pregnancyFields}
          validationSchema={pregnancySchema}
          showErrors={showErrors}
          onChange={state.set('pregnancy')}
          onChangeDebounceTime={0}
          showSubmitButton={false}
          hasCard={false}
          title={strings.FormCardLabelPregnant}
          titleVariant="subheading"
        />
      </Visible>

      <AllergyListForm
        title={strings.FormLabelAllergies}
        titleVariant="subheading"
        showSubmitButton={false}
        onValidChange={state.set('allergy')}
      />
    </Card>
  );
};
